import { CircleSolid } from "@ignite-analytics/icons";
import {
    Avatar,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Grid,
    List,
    ListItem,
    Stack,
    Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useSearchParams } from "react-router-dom";

import MessagePage from "../../components/MessagePage";
import { PoweredByFooter } from "../../components/PoweredByFooter";
import { Assessment } from "../../globals/types";
import { useAssessments } from "../../hooks/useFormsLists";
import { isNotFound } from "../../lib/formsApi";
import { LocalizedContent } from "../../lib/i18n/contentTranslations";

import { getAssessmentStatus } from "./getAssessmentStatus";

export const OverviewPage: React.FC = () => {
    const { data, error } = useAssessments();

    const { formatMessage } = useIntl();

    let supplierName = data?.[0]?.supplierName;
    let supplierInitials: string;
    if (supplierName) {
        supplierInitials = supplierName.charAt(0).toUpperCase();
    } else {
        supplierName = formatMessage({
            defaultMessage: "No supplier",
            description: "Fallback text when no supplier is available",
        });
        supplierInitials = formatMessage({
            defaultMessage: "?",
            description: "Fallback initials when no supplier is available",
        });
    }

    if (isNotFound(error) || data?.length === 0) {
        return <MessagePage reason="404" />;
    }

    if (error) {
        return <MessagePage reason="other" />;
    }

    return (
        <Container sx={{ height: "100vh", minHeight: "300px" }} disableGutters={false}>
            <Stack alignItems="center" justifyContent="flex-start" height="100%" gap={4}>
                <Stack height="75%" justifyContent="center" alignItems="center" py={4} width="100%">
                    <Card
                        sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 4.5,
                        }}
                    >
                        <CardHeader
                            title={
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar>{supplierInitials}</Avatar>
                                    <Typography variant="inherit">{supplierName}</Typography>
                                </Stack>
                            }
                            slotProps={{
                                title: {
                                    variant: "textXl",
                                    fontWeight: "bold",
                                },
                            }}
                            sx={{
                                padding: (t) => t.spacing(4, 4, 0, 4),
                            }}
                        />
                        <CardContent
                            sx={{
                                flex: "1 1 auto",
                                overflowY: "auto",
                                padding: (t) => t.spacing(0, 4, 4, 4),
                            }}
                        >
                            <List>
                                {data?.map((assessment) => (
                                    <AssessmentListItem key={assessment.id} assessment={assessment} />
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Stack>
                <Stack mb={12}>
                    <PoweredByFooter />
                </Stack>
            </Stack>
        </Container>
    );
};

interface AssessmentListItemProps {
    assessment: Assessment;
}

const AssessmentListItem: React.FC<AssessmentListItemProps> = ({ assessment }) => {
    const status = getAssessmentStatus(assessment);
    const [searchParams] = useSearchParams();

    let chip: React.ReactNode;
    switch (status) {
        case "Submitted": {
            chip = (
                <Chip
                    label={<FormattedMessage defaultMessage="Submitted" />}
                    color="success"
                    variant="status"
                    icon={<CircleSolid />}
                    iconSize="small"
                />
            );
            break;
        }
        case "InProgress": {
            chip = (
                <Chip
                    label={<FormattedMessage defaultMessage="In progress" />}
                    color="warning"
                    variant="status"
                    icon={<CircleSolid />}
                    iconSize="small"
                />
            );
            break;
        }
        case "NotStarted": {
            chip = (
                <Chip
                    label={<FormattedMessage defaultMessage="Not started" />}
                    color="neutral"
                    variant="status"
                    icon={<CircleSolid />}
                    iconSize="small"
                />
            );
            break;
        }
    }

    let button: React.ReactNode;
    if (status === "Submitted") {
        button = (
            <Button
                size="small"
                color="secondary"
                component={Link}
                to={{
                    pathname: `/invitation/${assessment.id}`,
                    search: `?${searchParams.toString()}`,
                }}
            >
                <FormattedMessage defaultMessage="View answers" description="View answers button label" />
            </Button>
        );
    } else {
        button = (
            <Button
                size="small"
                component={Link}
                to={{
                    pathname: `/invitation/${assessment.id}`,
                    search: `?${searchParams.toString()}`,
                }}
            >
                <FormattedMessage defaultMessage="Open" description="Open assessment button label" />
            </Button>
        );
    }

    return (
        <ListItem sx={{ width: "100%" }} disableGutters>
            <Card sx={{ width: "100%" }}>
                <CardContent sx={{ overflow: "hidden" }}>
                    <Grid
                        container
                        direction="row"
                        justifyContent={{
                            xs: "space-between",
                            sm: "flex-start",
                        }}
                        alignItems="center"
                        gap={1}
                    >
                        <Grid
                            size={{
                                xs: 12,
                                sm: 5,
                            }}
                        >
                            <Typography variant="textSm" fontWeight="medium">
                                <LocalizedContent
                                    translationKey={assessment.questionnaire.nameTranslationKey}
                                    fallback={assessment.questionnaire.name}
                                />
                            </Typography>
                        </Grid>
                        <Grid
                            size={{
                                xs: "auto",
                                sm: "grow",
                            }}
                        >
                            {chip}
                        </Grid>
                        <Grid
                            size={{
                                xs: "auto",
                            }}
                        >
                            {button}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </ListItem>
    );
};
